/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';
import path from 'path';
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import '../styles/brands.scss';

import { defineComponentMapping } from '../import-components';
import { useSessionDataStore } from '@marriott/mi-homepage-components-store';
import { UserSessionProvider } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { setSubDirectoryPrefix } from '@marriott/mi-luxury-components';
import { setSubDirectoryPrefix as setSubDirectoryPrefixForDynamicListing } from '@marriott/mi-brands-components/utils';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
  }
}

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync();
}

const App = function (props: any) {
  console.log('App.tsx props', props);
  const { Component, pageProps } = props;
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };
  const { resolvedUrl, model, isAuthorMode, NEXT_GLOBAL_ASSET_PREFIX, requestId, currentLocale, isDTT } = pageProps;
  const brandSlug = model?.brandSlug;
  const { setSessionData, setHeadersData, setEnvVariablesData } = useSessionDataStore();
  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  const isRtl = pageProps?.model?.rtlEnable === 'true' || pageProps?.rtlEnable === 'true'; // Default to LTR if not provided
  const mergeEnvVars = useClientEnvVarsStore((state: any) => state.mergeEnvVars);

  // WEB-152729: Overriding the session locale due to inconsistency while settng up the locale with and without underscore
  if (typeof window !== 'undefined' && window.document && window['sessionData']) {
    window.sessionData.locale = window?.sessionData?.locale?.replace('_', '-');
  }

  // adding dummy variable to fulfill empty env vars condition in session provider.
  // This needs to read from environemnt variables once websdk is enabled for this product
  mergeEnvVars({
    NEXT_PUBLIC_SUBMIT_SEARCH_URL: pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GOOGLE_MAP_API_KEY: pageProps?.GOOGLE_MAP_API_KEY,
    NEXT_PUBLIC_ENABLE_WEBSDK: 'false',
    REQUEST_ID: requestId,
    ACCEPT_LANGUAGE: currentLocale?.replace('_', '-'),
    IS_DTT: isDTT,
  });
  useEffect(() => {
    setSessionData(pageProps.sessionData);
    setHeadersData(pageProps.headersData);
    setEnvVariablesData({
      submitSearchUrl: process.env.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
      availabilitySearchUrl: process.env.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      googleMapApiKey: process.env.GOOGLE_MAP_API_KEY,
    });

    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (isRtl
          ? `global-styles/${globalStylesVersion}/marriot.global.rtl.css`
          : `global-styles/${globalStylesVersion}/marriot.global.css`);
      document.head.appendChild(globalStyle);

      // Adding brands styling on author mode
      if (brandSlug !== '' && brandSlug !== undefined) {
        const brandLink = document.createElement('link');
        const BRAND_SLUG_CODE = brandSlug.toUpperCase();
        brandLink.rel = 'stylesheet';
        brandLink.href = `${assetUrl}brand-framework/brand-config.${BRAND_SLUG_CODE}.css`;
        document.head.appendChild(brandLink);
      }
    }
  }, []);
  if (model) {
    setSubDirectoryPrefix(model.subDirectoryContextPath ? model.subDirectoryContextPath : '');
    setSubDirectoryPrefixForDynamicListing(model.subDirectoryContextPath ? model.subDirectoryContextPath : '');
  }

  defineComponentMapping(allowedComponents, resolvedUrl, isAuthorMode, pageProps, isRtl, brandSlug);

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });
  return (
    <StyleSheetManager stylisPlugins={isRtl ? [rtlPluginTs] : []}>
      <UserSessionProvider pageProps={pageProps}>
        <Component {...pageProps} />
      </UserSessionProvider>
    </StyleSheetManager>
  );
};

export default App;
